import { getNzDateTimeParts } from '@/utils/dates';

// A scheduled payment is only blocked from deletion while the bank is likely actively sending
// it - a daily NZT window, not a one-off cutoff. Change these two constants to adjust the window
// everywhere it's enforced.
export const DELETE_BLOCK_WINDOW_START_NZT = { hour: 7, minute: 30 } as const;
export const DELETE_BLOCK_WINDOW_END_NZT = { hour: 9, minute: 30 } as const;

/**
 * True once a scheduled payment's NZ calendar day has arrived (today, or already passed - an
 * overdue payment that's still APPROVED rather than FAILED) AND the current NZ wall-clock time
 * falls within the daily send window - the bank may be actively sending it right now, so deletion
 * is blocked. Outside that window - earlier the same day, later the same day, or on any day after
 * an overdue payment's scheduled day - deletion is safe again: either sending hasn't started yet
 * or today's window has already closed. A payment scheduled for a future NZ day is always safe to
 * delete regardless of the current time.
 *
 * Checked at the moment Delete is clicked (see openDeleteDialog in useScheduledPaymentAction),
 * not during render - the delete button itself always shows for an APPROVED/FAILED payment; only
 * clicking it reveals whether it's currently blocked (via UnableToDeleteModal).
 */
export const isWithinDeleteBlockWindow = (
  scheduledDate: string,
  now: Date = new Date()
): boolean => {
  const scheduled = getNzDateTimeParts(new Date(scheduledDate));
  const current = getNzDateTimeParts(now);

  const scheduledDay = Date.UTC(scheduled.year, scheduled.month - 1, scheduled.day);
  const currentDay = Date.UTC(current.year, current.month - 1, current.day);

  if (scheduledDay > currentDay) return false;

  const currentMinutes = current.hour * 60 + current.minute;
  const windowStartMinutes =
    DELETE_BLOCK_WINDOW_START_NZT.hour * 60 + DELETE_BLOCK_WINDOW_START_NZT.minute;
  const windowEndMinutes =
    DELETE_BLOCK_WINDOW_END_NZT.hour * 60 + DELETE_BLOCK_WINDOW_END_NZT.minute;
  return currentMinutes >= windowStartMinutes && currentMinutes < windowEndMinutes;
};
